import Layout from 'components/layout';

import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import { Heading } from 'lucid-components-react';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import FooterAbTest from 'components/ab-tests/footer-swap/footer-swap';
import NavbarAbTest from 'components/ab-tests/nav-swap/nav-swap';
import BackNForth from 'components/back-n-forth/back-n-forth';
import EmailOnly, { EmailOnlyThemes } from 'components/email-only/email-only';
import Link from 'components/link/link';
import Table from 'components/table/table';
import {
  Drupal_FieldNodeEduFieldEduBanner,
  Drupal_FieldNodeEduFieldEduTestimonial,
  Drupal_FieldNodeEduFieldFirstSectionCtas,
  Drupal_FieldNodeEduFieldHeroCta,
  Drupal_FieldNodePartnersFieldBackNForthSection,
  Drupal_NodeEdu,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphEduBanner,
  Drupal_ParagraphMarketoForm,
  Drupal_ParagraphTestimonial
} from 'generated/graphql';
import useMarketoLink from 'hooks/use-marketo-link';
import { useMenuDefaultFooter, useMenuMainNavLinks } from 'hooks/use-menu';
import { TemplateProps } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';
import { getProcessedValue } from 'utils/helpers';
import linkParser from 'utils/link-parser';
import './edu.scss';

const EDU = ({ data, pageContext, location }: TemplateProps) => {
  const { langPrefix, pageType, nid, i18nKey, hideSsr } = pageContext;

  const { i18nData } = data;
  const drupal = data.drupal;
  const node = drupal.nodeById as Drupal_NodeEdu;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const i18n = i18nFunct(i18nData, i18nKey);

  const backNForthSections =
    node.fieldBackNForthSection!.length > 0
      ? (node.fieldBackNForthSection as Drupal_FieldNodePartnersFieldBackNForthSection[])
      : null;

  const emailOnlyOrTextColor = 'white';

  const navbarMenu = useMenuMainNavLinks(langPrefix);
  const footerMenu = useMenuDefaultFooter(langPrefix);

  const getMarketoLink = useMarketoLink({
    i18nKey,
    langPrefix
  });

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
      trackers={{
        personas: node.fieldPersona
      }}
    >
      <NavbarAbTest menu={navbarMenu} scrollDistance={10} />
      <main className="edu">
        <section
          className="edu__hero"
          style={{
            backgroundImage: `url(${node.fieldHeroImage ||
              'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/press/education-page/classroom@2x.jpg'})`
          }}
        >
          <div className="lucid-container">
            {node.fieldTitle ? (
              <Heading
                as="h1"
                color="white"
                mb={0}
                py={5}
                textAlign="center"
                variant="headlineS"
              >
                {node.fieldTitle}
              </Heading>
            ) : (
                /* eslint-disable-next-line react/jsx-indent */
                <Heading
                  as="h1"
                  color="white"
                  dangerouslySetInnerHTML={{
                    __html: i18n('mktg-ct-edu-usecase-hero-section-h1', {}, [
                      '<span className="edu__title--bold">{}</span>'
                    ])
                  }}
                  mb={0}
                  py={5}
                  textAlign="center"
                  variant="headlineS"
                />
              )}

            {node.fieldSubTitle ? (
              <p className="edu__subtitle  edu__subtitle--white">
                {node.fieldSubTitle}
              </p>
            ) : (
                /* eslint-disable-next-line react/jsx-indent */
                <>
                  <p className="edu__subtitle  edu__subtitle--white">
                    {i18n('mktg-ct-edu-usecase-hero-section-subtitle')}
                  </p>
                  <p className="edu__subtitle  edu__subtitle--white">
                    {i18n('mktg-ct-edu-usecase-hero-section-button-context')}
                  </p>
                </>
              )}

            {node.fieldHasEmailOnly ? (
              <div className="edu__cta-container">
                <EmailOnly
                  orTextColor={emailOnlyOrTextColor}
                  theme={EmailOnlyThemes.Retro}
                />
              </div>
            ) : (
                /* eslint-disable-next-line react/jsx-indent */
                <div className="edu__cta-wrapper">
                  {node.fieldHeroCta &&
                    node.fieldHeroCta.length > 0 &&
                    node.fieldHeroCta[0]!.entity &&
                    (node.fieldHeroCta as Drupal_FieldNodeEduFieldHeroCta[]).map(
                      cta => {
                        const entity = cta.entity as Drupal_ParagraphCtaButton;
                        if (entity.fieldButtonLink) {
                          return (
                            <Link
                              key={entity.fieldButtonText}
                              inverse
                              className={
                                entity.fieldIsSecondary ? 'pad--left' : ''
                              }
                              primary={!entity.fieldIsSecondary}
                              secondary={!!entity.fieldIsSecondary}
                              to={entity.fieldButtonLink}
                            >
                              {entity.fieldButtonText}
                            </Link>
                          );
                        }

                        const marketoFormEntity = entity.fieldMarketo
                          ? (entity.fieldMarketo
                            .entity as Drupal_ParagraphMarketoForm)
                          : null;

                        if (marketoFormEntity) {
                          const { url, copy } = getMarketoLink(
                            marketoFormEntity.fieldMarketoId
                          );
                          return (
                            <Link
                              key={entity.fieldButtonText}
                              asButton
                              primary={!entity.fieldIsSecondary}
                              secondary={!!entity.fieldIsSecondary}
                              to={url}
                            >
                              {entity.fieldButtonText || copy}
                            </Link>
                          );
                        }
                        return;
                      }
                    )}
                </div>
              )}
          </div>
        </section>

        <section className="edu__first-section lucid-container lucid-container--flex">
          <div className="edu__container--half">
            <Heading as="h2" mt={3} variant="subheadM">
              {node.fieldFirstSectionHeader}
            </Heading>
            <div className="edu__first-section__text">
              {ReactHtmlParser(
                getProcessedValue(node.fieldFirstSectionSubtitle),
                {
                  transform: linkParser
                }
              )}
            </div>

            {node.fieldHasEmailOnly ? (
              <div className="edu__cta-container">
                <EmailOnly
                  hideSSO
                  placeholder={i18n('mktg_email_only_edu_placeholder')}
                  theme={EmailOnlyThemes.Retro}
                />
              </div>
            ) : (
                /* eslint-disable-next-line react/jsx-indent */
                <div>
                  {node.fieldFirstSectionCtas &&
                    node.fieldFirstSectionCtas.length > 0 &&
                    node.fieldFirstSectionCtas[0]!.entity &&
                    (node.fieldFirstSectionCtas as Drupal_FieldNodeEduFieldFirstSectionCtas[]).map(
                      cta => {
                        const entity = cta.entity as Drupal_ParagraphCtaButton;
                        if (entity.fieldButtonLink) {
                          return (
                            <Link
                              key={entity.fieldButtonText}
                              secondary
                              className={`${
                                entity.fieldIsSecondary
                                  ? 'link--white  pad--left'
                                  : ''
                                }`}
                              to={entity.fieldButtonLink}
                            >
                              {entity.fieldButtonText}
                            </Link>
                          );
                        }

                        const marketoFormEntity = entity.fieldMarketo
                          ? (entity.fieldMarketo
                            .entity as Drupal_ParagraphMarketoForm)
                          : null;

                        if (marketoFormEntity) {
                          const { url, copy } = getMarketoLink(
                            marketoFormEntity.fieldMarketoId
                          );
                          return (
                            <Link
                              key={entity.fieldButtonText}
                              asButton
                              primary={!entity.fieldIsSecondary}
                              secondary={!!entity.fieldIsSecondary}
                              to={url}
                            >
                              {entity.fieldButtonText || copy}
                            </Link>
                          );
                        }
                      }
                    )}
                </div>
              )}
          </div>
          <div className="edu__container--half  edu__laptop">
            <img
              alt={node.fieldFirstSectionHeader!}
              className="edu__laptop--img"
              src={node.fieldFirstSectionImage!}
            />
          </div>
        </section>

        <div className={`${node.fieldHasEmailOnly ? '' : 'hide--learn-more'}`}>
          <Table />
        </div>

        {backNForthSections && (
          <BackNForth sections={backNForthSections} useJumplinks={false} />
        )}
        {node.fieldTestimonialHeader && (
          <section className="edu__testimonials edu__testimonials--background">
            <div className="lucid-container lucid-container--flex">
              <Heading
                as="h2"
                color="white"
                m={0}
                py={5}
                textAlign="center"
                variant="subheadS"
              >
                {node.fieldTestimonialHeader}
              </Heading>
              {(node.fieldEduTestimonial as Drupal_FieldNodeEduFieldEduTestimonial[]).map(
                testimonial => {
                  const entity = testimonial.entity as Drupal_ParagraphTestimonial;
                  return (
                    <div
                      key={entity.fieldQuoteAttribution}
                      className="edu__testimonial"
                    >
                      <p className="edu__quote">{entity.fieldQuoteBody}</p>
                      <p className="edu__attribution">
                        {entity.fieldQuoteAttribution}
                      </p>
                    </div>
                  );
                }
              )}
            </div>
          </section>
        )}
        {node.fieldEduBanner!.length > 0 &&
          (node.fieldEduBanner as Drupal_FieldNodeEduFieldEduBanner[]).map(
            banner => {
              const bannerItem = banner.entity as Drupal_ParagraphEduBanner;
              return (
                <section
                  key={bannerItem.fieldTitle}
                  className={`edu__banner
                        ${
                    bannerItem.fieldOrangeBackground
                      ? 'edu__background--dark'
                      : ''
                    }
                      `}
                >
                  <div className="lucid-container lucid-container--flex">
                    <Heading
                      as="h2"
                      color={
                        bannerItem.fieldOrangeBackground
                          ? 'white'
                          : 'subheading'
                      }
                      m={0}
                      py={4}
                      textAlign="center"
                      variant="subheadM"
                      width="100%"
                    >
                      {bannerItem.fieldTitle}
                    </Heading>
                    <p
                      className={`edu__subtitle  edu__title--full-width  edu__title--center  ${
                        bannerItem.fieldOrangeBackground
                          ? 'edu__title--white'
                          : 'edu__title--blue-steel'
                        }`}
                    >
                      {bannerItem.fieldSubtitle}
                    </p>

                    {bannerItem.fieldCtaButton &&
                      bannerItem.fieldCtaButton.length > 0 &&
                      bannerItem.fieldCtaButton[0]!.entity && (
                        <div className="edu__banner__cta-wrapper">
                          {(bannerItem.fieldCtaButton as Drupal_FieldNodeEduFieldEduBanner[]).map(
                            cta => {
                              const { entity } = cta as {
                                entity: Drupal_ParagraphCtaButton;
                              };
                              if (entity.fieldButtonLink) {
                                return (
                                  <Link
                                    key={entity.fieldButtonText}
                                    asButton
                                    primary={!entity.fieldIsSecondary}
                                    secondary={!!entity.fieldIsSecondary}
                                    to={entity.fieldButtonLink}
                                  >
                                    {entity.fieldButtonText}
                                  </Link>
                                );
                              }

                              const eduBannerMarketoForm = entity.fieldMarketo
                                ? (entity.fieldMarketo
                                  .entity as Drupal_ParagraphMarketoForm)
                                : null;

                              if (eduBannerMarketoForm) {
                                const { url, copy } = getMarketoLink(
                                  eduBannerMarketoForm.fieldMarketoId
                                );
                                return (
                                  <Link
                                    key={entity.fieldButtonText}
                                    asButton
                                    primary={!entity.fieldIsSecondary}
                                    secondary={!!entity.fieldIsSecondary}
                                    to={url}
                                  >
                                    {entity.fieldButtonText || copy}
                                  </Link>
                                );
                              }
                            }
                          )}
                        </div>
                      )}
                  </div>
                </section>
              );
            }
          )}
      </main>
      <FooterAbTest entityTranslations={entityTranslations} menu={footerMenu} />
    </Layout>
  );
};

export default EDU;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeEdu {
          ...entityFields
          fieldTitle
          fieldSubTitle
          fieldHeroCta {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonText
                fieldButtonLink
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
          fieldHeroImage
          fieldHasEmailOnly
          fieldFirstSectionHeader
          fieldFirstSectionImage
          fieldFirstSectionSubtitle {
            processed
          }
          fieldFirstSectionCtas {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonText
                fieldButtonLink
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
          fieldTestimonialHeader
          fieldEduTestimonial {
            entity {
              ... on Drupal_ParagraphTestimonial {
                fieldQuoteBody
                fieldQuoteAttribution
              }
            }
          }
          fieldEduBanner {
            entity {
              ... on Drupal_ParagraphEduBanner {
                fieldTitle
                fieldSubtitle
                fieldOrangeBackground
                fieldCtaButton {
                  entity {
                    ... on Drupal_ParagraphCtaButton {
                      fieldButtonText
                      fieldButtonLink
                      fieldIsSecondary
                      fieldMarketo {
                        entity {
                          ... on Drupal_ParagraphMarketoForm {
                            fieldMarketoId
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          fieldBackNForthSection {
            entity {
              ... on Drupal_ParagraphBackNForthSection {
                fieldTitle
                fieldSectionDescription {
                  processed
                }
                fieldSectionImage {
                  entity {
                    ... on Drupal_ParagraphSeoImage {
                      fieldImageLink
                      fieldAltText
                    }
                  }
                }
                fieldCtaButton {
                  entity {
                    ... on Drupal_ParagraphCtaButton {
                      fieldButtonLink
                      fieldButtonText
                      fieldIsSecondary
                      fieldMarketo {
                        entity {
                          ... on Drupal_ParagraphMarketoForm {
                            fieldMarketoId
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          fieldPersona {
            entity {
              entityLabel
            }
          }
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_all_sign_up_free
            mktg_ct_edu_usecase_hero_section_h1
            mktg_ct_edu_usecase_hero_section_subtitle
            mktg_ct_edu_usecase_hero_section_button_context
            mktg_email_only_edu_placeholder
          }
        }
      }
    }
  }
`;
